import { useEffect, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { FiX, FiTrash2 } from "react-icons/fi";
import { toast } from "react-hot-toast";
import {
  useCreateCommitteMutation,
  useGetAppraisalObjectivesQuery,
  useGetHrDepartmentsQuery,
  useGetRatingsSelectQuery,
} from "../../../../../../api/hrApi";
import DashboardStyle from "../../../../../../dashboard/Styles/Dashboard.module.css";
import { ActionButtons } from "../../../../../../global/components/Buttons/buttons";
import {
  FormInput,
  FormSelect,
  FormTemplate,
  FormTextArea,
} from "../../../../../Components/Forms/InputTemplate";
import { appraisalPeriod, MeasurementType, currencies } from ".././data";
import plus from "../../../../../Images/plus.svg";

// Individual KRA Form Component
const KRAFormSection = ({
  kraId,
  departments,
  formMethods,
  onRemove,
  isRemovable,
}) => {
  const getFieldName = (field) => (kraId === 1 ? field : `${field}_${kraId}`);

  // Watch measurement type and department for this specific KRA
  const watchMeasurementType = useWatch({
    control: formMethods.control,
    name: getFieldName("measurementType"),
  });
  const watchDepartment = useWatch({
    control: formMethods.control,
    name: getFieldName("departmentId"),
  });
  const watchRateId = useWatch({
    control: formMethods.control,
    name: getFieldName("rateId"),
  });

  // Get ratings data
  const { data: ratings } = useGetRatingsSelectQuery({
    ApprovalStatus: 0,
  });

  // Get current measurement type (from watch or fallback)
  const currentMeasurementType = watchMeasurementType;

  // Fetch objectives for this specific KRA's department
  const { data: kraObjectives } = useGetAppraisalObjectivesQuery(
    {
      ApprovalStatus: 0,
      departmentId: watchDepartment,
      PageSize: 1000,
      PageNumber: 1,
    },
    {
      skip: !watchDepartment || watchDepartment == 0, // Skip if no department selected
    }
  );

  // Use the objectives from the API call
  const filteredObjectives = kraObjectives?.result?.filter(dat => (dat?.objectiveType?.toLowerCase() === "committee") )|| [];
  
  useEffect(() => {
    if (watchMeasurementType) {
      const measurementType = MeasurementType.find(
        (type) => type.id === parseInt(watchMeasurementType)
      );
      if (measurementType?.name === "Yes/No") {
        formMethods.setValue(getFieldName("targetValue"), "yesNo");
      } else {
        // Clear targetValue for other measurement types to allow user input
        if (measurementType?.name !== "Yes/No") {
          // Only clear if it was previously set to "yesNo"
          const currentValue = formMethods.getValues(
            getFieldName("targetValue")
          );
          if (currentValue === "yesNo") {
            formMethods.setValue(getFieldName("targetValue"), "");
          }
        }
      }
    }
  }, [watchMeasurementType, formMethods, kraId]);

  // Update target value when rating is selected (for Rating measurement type)
  useEffect(() => {
    if (
      parseInt(watchMeasurementType) === 4 &&
      watchRateId &&
      parseInt(watchRateId) > 0
    ) {
      // Find the selected rating
      const selectedRating = ratings?.data?.find(
        (rating) => rating.id === parseInt(watchRateId)
      );

      if (selectedRating && selectedRating.scaleLevels) {
        // Find the highest scaleNumber from the scaleLevels
        const highestScale = Math.max(
          ...selectedRating.scaleLevels.map((scale) => scale.scaleNumber)
        );

        // Set the target value to the highest scale number
        formMethods.setValue(getFieldName("targetValue"), highestScale);
        console.log(
          "Setting target value to highest scale:",
          highestScale,
          "for rating:",
          selectedRating.title
        );
      }
    }
  }, [watchMeasurementType, watchRateId, ratings, formMethods, kraId]);

  return (
    <div className="border border-gray-200 rounded-lg p-6 mb-6 relative">
      {/* Header with KRA title and remove button */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800">KRA #{kraId}</h3>
        {isRemovable && (
          <button
            type="button"
            onClick={() => onRemove(kraId)}
            className="text-red-500 hover:text-red-700 cursor-pointer transition-colors p-1"
            title="Remove KRA"
          >
            <FiTrash2 size="1.2rem" />
          </button>
        )}
      </div>

      {/* KRA Form Fields */}
      <div>
        <FormInput
          title={"Key Result Title"}
          camelCase={getFieldName("keyResultTitle")}
          placeholder={"Enter key result title"}
        />
        <FormTextArea
          className="h-[200px]"
          title={"Description"}
          camelCase={getFieldName("keyResultDescription")}
        />
      </div>

      <div className="grid gap-5 grid-cols-2">
        <FormSelect
          title={"Department"}
          camelCase={getFieldName("departmentId")}
          placeholder={"select"}
          array={departments?.responseObject?.pageItems?.map((opt) => (
            <option key={opt.id} value={opt.id}>
              {opt?.name}
            </option>
          ))}
          defaultValue={0}
        />
        <FormSelect
          title={"Objective"}
          camelCase={getFieldName("objectiveId")}
          placeholder={"select"}
          array={filteredObjectives.map((opt) => (
            <option key={opt.id} value={opt.id}>
              {opt?.title} {opt?.designation?.designationName}
            </option>
          ))}
          defaultValue={0}
        />
        <FormInput
          title={"Key Result Type"}
          camelCase={getFieldName("keyResultType")}
          placeholder={"select"}
          value="Non Core"
          disabled={true}
          isOptional={true}
        />
        {(parseInt(currentMeasurementType) === 1 ||
          parseInt(currentMeasurementType) === 3) && (
          <FormInput
            title={"Threshold"}
            camelCase={getFieldName("threshold")}
            placeholder={"Enter value"}
            type={"number"}
            max={parseInt(currentMeasurementType) === 3 ? 100 : undefined}
          />
        )}

        {/* Show threshold for Currency (editable) */}
        {parseInt(currentMeasurementType) === 2 && (
          <FormInput
            title={"Threshold"}
            camelCase={getFieldName("threshold")}
            placeholder={"Enter threshold value"}
            type={"number"}
          />
        )}

        {/* Show threshold for other measurement types */}
        {parseInt(currentMeasurementType) === 4 && (
          <FormInput
            title={"Threshold"}
            camelCase={getFieldName("threshold")}
            placeholder={"Enter threshold value"}
            type={"number"}
          />
        )}
        <FormSelect
          title={"Measurement Type"}
          camelCase={getFieldName("measurementType")}
          placeholder={"select"}
          array={MeasurementType.map((opt) => (
            <option key={opt.id} value={opt.id}>
              {opt.name}
            </option>
          ))}
          defaultValue={0}
        />

        {parseInt(currentMeasurementType) === 2 && (
          <FormSelect
            title={"Currency Type"}
            camelCase={getFieldName("currencyType")}
            placeholder={"select"}
            array={[
              <option key="empty" value="">
                Select Currency
              </option>,
              ...currencies.map((opt) => (
                <option key={opt.name} value={opt.name}>
                  {opt.name}
                </option>
              )),
            ]}
            defaultValue={""}
          />
        )}

        {parseInt(currentMeasurementType) === 4 && (
          <FormSelect
            title={"Rating"}
            camelCase={getFieldName("rateId")}
            placeholder={"select"}
            array={[
              <option key="empty" value={0}>
                Select Rating
              </option>,
              ...(ratings?.data?.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.title}
                </option>
              )) || []),
            ]}
            defaultValue={0}
          />
        )}

        {parseInt(currentMeasurementType) === 5 && (
          <FormInput
            title={"Yes/No Value"}
            camelCase={getFieldName("targetValue")}
            placeholder={"Yes/No"}
            type={"text"}
            disabled={true}
            value="yesNo"
            defaultValue="yesNo"
          />
        )}

        {(parseInt(currentMeasurementType) === 1 ||
          parseInt(currentMeasurementType) === 3) && (
          <FormInput
            title={"Target Value"}
            camelCase={getFieldName("targetValue")}
            placeholder={"Enter value"}
            type={"number"}
            max={parseInt(currentMeasurementType) === 3 ? 100 : undefined}
          />
        )}

        {parseInt(currentMeasurementType) === 2 && (
          <FormInput
            title={"Target Value"}
            camelCase={getFieldName("targetValue")}
            placeholder={"Enter currency value"}
            type={"number"}
          />
        )}

        {/* Show threshold for Rating (auto-populated from highest scale) */}
        {parseInt(currentMeasurementType) === 4 &&
          watchRateId &&
          parseInt(watchRateId) > 0 && (
            <FormInput
              title={"Target Value"}
              camelCase={getFieldName("targetValue")}
              placeholder={"Auto-populated from rating scale"}
              type={"number"}
              disabled={true}
            />
          )}
        <FormInput
          title={"Weight"}
          camelCase={getFieldName("weight")}
          placeholder={"Enter weight"}
          type={"number"}
        />
        <FormSelect
          title={"Appraisal Period"}
          camelCase={getFieldName("appraisalPeriod")}
          placeholder={"select"}
          array={appraisalPeriod.map((opt) => (
            <option key={opt.filter} value={opt.filter}>
              {opt?.name}
            </option>
          ))}
          defaultValue={0}
        />
        <FormInput
          title={"Period Start Date"}
          camelCase={getFieldName("periodStartDate")}
          placeholder={"select"}
          type={"date"}
        />
        <FormInput
          title={"Self-Appraisal SLA (hours)"}
          camelCase={getFieldName("selfAppraisalHours")}
          placeholder={"Enter hours"}
          type={"number"}
        />
        <FormInput
          title={"Reviewers/Approvers SLA (hours)"}
          camelCase={getFieldName("reviewerHours")}
          placeholder={"Enter hours"}
          type={"number"}
        />
        <FormInput
          title={"Number of SLA Reminder"}
          camelCase={getFieldName("reminderCount")}
          placeholder={"Enter count"}
          type={"number"}
        />
      </div>
    </div>
  );
};

export const CommitteKRAModal = ({
  setIsOpen,
  isOpen,
  onValidate,
  onBack = () => {},
  title,
  description,
  committeeHeadId,
  departmentId,
  memberIds,
}) => {
  const [kraForms, setKraForms] = useState([{ id: 1 }]);
  const [onCreate, { isLoading }] = useCreateCommitteMutation();
  const formMethods = useForm({
    mode: "all",
  });
  const {
    handleSubmit,
    formState: { isValid },
    control,
  } = formMethods;
  const { data: departments } = useGetHrDepartmentsQuery();
  // Each KRA fetches its own objectives based on selected department
  // Measurement type handling is now done per individual KRA

  // Function to add a new KRA form
  const addMoreKRA = () => {
    const newKRAId = kraForms.length + 1;
    setKraForms([...kraForms, { id: newKRAId }]);
  };

  // Function to remove a KRA form
  const removeKRA = (kraId) => {
    if (kraForms.length > 1) {
      setKraForms(kraForms.filter((form) => form.id !== kraId));
    }
  };

  const submit = async (data) => {
    // Build KRAs array from multiple form data
    const krAs = kraForms.map((kraForm) => {
      const kraId = kraForm.id;
      const getFieldValue = (field) => {
        return kraId === 1 ? data[field] : data[`${field}_${kraId}`];
      };

      // Handle target value processing
      let processedTargetValue = getFieldValue("targetValue");
      if (parseInt(getFieldValue("measurementType")) === 5) {
        processedTargetValue = "yesNo";
      } else if (processedTargetValue !== "yesNo") {
        processedTargetValue = parseInt(processedTargetValue) || 0;
      }

      return {
        objectiveId: parseInt(getFieldValue("objectiveId")) || 0,
        keyResultTitle: getFieldValue("keyResultTitle") || "",
        keyResultDescription: getFieldValue("keyResultDescription") || "",
        isFloatable: getFieldValue("isFloatable") || false,
        threshold: parseInt(getFieldValue("threshold")) || 0,
        targetValue: processedTargetValue,
        measurementType: parseInt(getFieldValue("measurementType")) || 0,
        currencyType: getFieldValue("currencyType") || "",
        rateId: parseInt(getFieldValue("rateId")) || 0,
        appraisalPeriod: parseInt(getFieldValue("appraisalPeriod")) || 0,
        periodStartDate: getFieldValue("periodStartDate") || "",
        weight: parseInt(getFieldValue("weight")) || 0,
        type: 1,
        reviewerHours: parseInt(getFieldValue("reviewerHours")) || 0,
        selfAppraisalHours: parseInt(getFieldValue("selfAppraisalHours")) || 0,
        reminderCount: parseInt(getFieldValue("reminderCount")) || 0,
      };
    });

    const payload = {
      title,
      description,
      committeeHeadId,
      departmentId,
      memberIds,
      krAs,
    };

    try {
      const response = await onCreate(payload).unwrap();
      if (response?.successful) {
        setIsOpen(false);
        onValidate();
        toast.success(response?.statusMessage);
      }
    } catch (err) {
      toast.error(err?.data?.statusMessage);
    }
  };

  return (
    <div className={DashboardStyle.dash_board_home_nav}>
      <div className={DashboardStyle.dash_board_home_nav_header}>
        <div>
          <h4 className="text-[25px] font-[500]">Set Up</h4>
          <h1 className="text-[28px] font-[800]">key Result Area</h1>
          <p>
            Define measurable outcomes for your selected objectives by creating
            structured KRAs (Key Result Areas)
          </p>
        </div>
        <FiX
          style={{ cursor: "pointer" }}
          size={"1.5rem"}
          onClick={() => setIsOpen(!isOpen)}
        />
      </div>
      <div className={DashboardStyle.dash_board_home_nav_body}>
        <div className="flex items-center justify-between mb-4">
          <div className="w-10" />
          <button
            type="button"
            onClick={addMoreKRA}
            className="flex items-center gap-x-2 h-auto text-[#A0BDA2] hover:text-[#8BA88D] cursor-pointer transition-colors"
          >
            <img src={plus} alt="add icon" /> <span>Add more KRA</span>
          </button>
        </div>
        {isLoading && <small>Loading...</small>}
        <div>
          <FormProvider {...formMethods}>
            <FormTemplate handleSubmit={handleSubmit(submit)}>
              {/* Render multiple KRA forms */}
              {kraForms.map((kraForm) => (
                <KRAFormSection
                  key={kraForm.id}
                  kraId={kraForm.id}
                  departments={departments}
                  formMethods={formMethods}
                  onRemove={removeKRA}
                  isRemovable={kraForms.length > 1}
                />
              ))}

              <div
                style={{ marginTop: "1rem" }}
                className="flex justify-between items-center"
              >
                <button 
                  type="button" 
                  onClick={onBack}
                  className="bg-gray-100 text-gray-600 px-4 py-2 rounded-sm hover:bg-gray-200"
                >
                  Back
                </button>
                <ActionButtons
                  isLoading={isLoading}
                  disabled={!isValid}
                  width={"auto"}
                  className={DashboardStyle?.button_cage_weight}
                  bg="var(--primary-color)"
                >
                  {isLoading ? "Creating..." : "Create"}
                </ActionButtons>
              </div>
            </FormTemplate>
          </FormProvider>
        </div>
      </div>
    </div>
  );
};
